import { useEffect, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import QRCode from '../components/QRCodeComponent';
import QRCodeLib from 'qrcode';
import './CompanyDashboard.css';
import { useCompany } from '../hooks/useCompany';
import { NoticeModal } from '../components/Modals';

const CompanyDashboard = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeMsg, setNoticeMsg] = useState('');

  const showNotice = (msg) => {
    setNoticeMsg(msg);
    setNoticeOpen(true);
  };

  // useCompanyフックを使用して企業情報を取得
  const { data, error, isLoading } = useCompany(companyId);

  // 企業情報が取得できた場合の処理
  useEffect(() => {
    if (data?.company?.id) {
      localStorage.setItem('company_id', data?.company?.id);
    }
  }, [data]);

  // エラーハンドリング
  useEffect(() => {
    if (error) {
      console.error('会社情報取得失敗:', error);
      navigate('/adminpage');
    }
  }, [error, navigate]);

  const oneTimePlans = data?.plans?.filter(
    (plan) => plan.interval === 'one_time'
  );
  const subscriptionPlans = data?.plans?.filter(
    (plan) => plan.interval === 'month'
  );

  // URL生成の共通関数
  const generateCheckoutUrl = (companyId, priceId) => {
    const baseUrl = process.env.REACT_APP_ORIGIN;
    if (!baseUrl) {
      console.error('REACT_APP_ORIGIN環境変数が設定されていません');
      return null;
    }

    if (!companyId || !priceId) {
      console.error('companyIdまたはpriceIdが無効です:', {
        companyId,
        priceId,
      });
      return null;
    }

    const params = new URLSearchParams({
      companyId: companyId.toString(),
      price_id: priceId.toString(),
    });

    return `${baseUrl}/login?${params.toString()}`;
  };

  const qrUrls = data?.company?.id
    ? data?.plans?.reduce((acc, option) => {
        const url = generateCheckoutUrl(data?.company?.id, option.price_id);
        if (url) acc[option.price_id] = url;
        return acc;
      }, {})
    : {};

  // QRコードのダウンロード
  const downloadQRcode = useCallback(
    async (url, label) => {
      try {
        const dataUrl = await QRCodeLib.toDataURL(url);
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = `${data.company.company_name}_${label}_QR.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('QRコードのダウンロードに失敗しました:', error);
      }
    },
    [data]
  );

  // リンクをクリップボードにコピー
  const copyLinkToClipboard = useCallback(
    (option) => {
      if (!data?.company?.id) return;

      const url = generateCheckoutUrl(data?.company?.id, option.price_id);

      if (!url) {
        showNotice('URLの生成に失敗しました');
        return;
      }

      if (!navigator.clipboard) {
        showNotice('コピー機能が利用できません');
        return;
      }

      navigator.clipboard
        .writeText(url)
        .then(() => {
          showNotice('リンクをコピーしました！');
        })
        .catch(() => {
          showNotice('コピーに失敗しました');
        });
    },
    [data?.company?.id]
  );

  if (isLoading) return <p>読み込み中...</p>;
  if (error) return <p>エラーが発生しました</p>;
  if (!data) return <p>企業情報が見つかりません</p>;

  return (
    <div>
      <div className="row">
        <div className="column">
          <h1>{data?.company?.company_name} のダッシュボード</h1>

          <div className="paymentQR">
            {/* 単発支払い */}
            <div style={{ marginBottom: '50px', padding: 10 }}>
              <h2>単発支払いのQRコード</h2>
              <div className="scrollQR">
                {oneTimePlans
                  .filter((option) => {
                    const campaign = data.campaigns.find(
                      (c) => c.price_id === option.price_id
                    );
                    return !campaign || campaign.is_active;
                  })
                  .map((option) => {
                    const url = qrUrls[option.price_id];
                    return (
                      <div
                        key={option.price_id}
                        style={{ marginBottom: '10px' }}
                      >
                        <p className="planLabel">{option.name}</p>
                        {url ? (
                          <div className="button-outline">
                            <QRCode value={url} size={180} />
                            <div className="column">
                              <br />
                              <button
                                onClick={() => downloadQRcode(url, option.name)}
                                className="dashboardButton1"
                              >
                                QRコードをダウンロード
                              </button>
                              <button
                                onClick={() => copyLinkToClipboard(option)}
                                className="dashboardButton2"
                              >
                                リンクをコピー
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p>QRコード生成中...</p>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* サブスクリプション支払い */}
            <div style={{ marginBottom: '50px', padding: 10 }}>
              <h2>サブスクリプションのQRコード</h2>
              <div className="scrollQR">
                {subscriptionPlans
                  .filter((option) => {
                    const campaign = data.campaigns.find(
                      (c) => c.price_id === option.price_id
                    );
                    return !campaign || campaign.is_active;
                  })
                  .map((option) => {
                    const url = qrUrls[option.price_id];
                    return (
                      <div
                        key={option.price_id}
                        style={{ marginBottom: '20px' }}
                      >
                        <p className="planLabel">{option.name}</p>
                        {url ? (
                          <div className="button-outline">
                            <QRCode value={url} size={180} />
                            <div className="column">
                              <br />
                              <button
                                onClick={() =>
                                  downloadQRcode(url, option.label)
                                }
                                className="dashboardButton1"
                              >
                                QRコードをダウンロード
                              </button>
                              <button
                                onClick={() => copyLinkToClipboard(option)}
                                className="dashboardButton2"
                              >
                                リンクをコピー
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p>QRコード生成中...</p>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <button
        type="button"
        className="backButtonFixed"
        onClick={() => navigate('/adminpage')}
      >
        管理画面に戻る
      </button>
      <NoticeModal
        open={noticeOpen}
        message={noticeMsg}
        onClose={() => setNoticeOpen(false)}
      />
    </div>
  );
};

export default CompanyDashboard;
